import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, get, setCsrfToken } from "../api/client";
import type { Session } from "../api/types";

export const sessionKey = ["session"] as const;

export async function fetchSession(): Promise<Session | null> {
  try {
    const s = await get<Session>("/auth/session");
    setCsrfToken(s.csrfToken);
    return s;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      setCsrfToken(null);
      return null;
    }
    throw err;
  }
}

export function useSession() {
  return useQuery({ queryKey: sessionKey, queryFn: fetchSession, staleTime: 60_000, retry: false, refetchOnWindowFocus: true });
}

/** Replaces the cached session after login, MFA, or organization switch. */
export function useSetSession() {
  const qc = useQueryClient();
  return (s: Session | null) => {
    setCsrfToken(s?.csrfToken ?? null);
    qc.setQueryData(sessionKey, s);
  };
}

/**
 * UI-only permission check. The server enforces every permission independently; this only hides or
 * disables controls the user cannot use.
 */
export function useCan() {
  const { data } = useSession();
  const perms = new Set(data?.permissions ?? []);
  const platform = new Set(data?.platformPermissions ?? []);
  return {
    can: (p: string) => perms.has(p),
    canAny: (...p: string[]) => p.some((x) => perms.has(x)),
    canPlatform: (p: string) => platform.has(p),
    hasPlatformAccess: platform.size > 0,
  };
}
